import type {
  FamilyEvent, FamilyPhoto, FuzzyDate, MediaAsset, RecordingDraft, Story, StoryContribution, StoryEvent, StoryPerson, StorySuggestion,
  Transcript, UploadSession, UUID,
} from '../../../domain/types';
import { seedId } from '../../../lib/uuid';
import { daysAgoISO } from '../../../lib/time';
import { placeholderWav } from './audio';
import { putBlob, mediaKey } from '../blobStore';
import { A, FAMILY_ID, L, P } from './people';

export const S = (k: string): UUID => seedId('story', k);
export const C = (k: string): UUID => seedId('contribution', k);
export const M = (k: string): UUID => seedId('media', k);
export const E = (k: string): UUID => seedId('event', k);

const ex = (d: string): FuzzyDate => ({ kind: 'exact', start: d, certainty: 'confirmed' });
const yr = (y: number): FuzzyDate => ({ kind: 'year', start: String(y), certainty: 'confirmed' });
const about = (y: number, text?: string): FuzzyDate => ({ kind: 'about', start: String(y), certainty: 'approximate', displayText: text });
const decade = (y: number): FuzzyDate => ({ kind: 'decade', start: String(y), end: String(y + 9), certainty: 'approximate' });
const month = (ym: string): FuzzyDate => ({ kind: 'month', start: ym, certainty: 'confirmed' });

interface ContribSpec {
  key: string;
  speaker: string;
  uploader: string;
  kind: 'audio' | 'text';
  text?: string;
  seconds?: number;
  daysAgo: number;
  dateInterpretation?: FuzzyDate;
  transcript?: { lang: 'en' | 'he'; text: string; status: Transcript['status']; error?: string };
  media?: { status: MediaAsset['status']; backup: MediaAsset['backupState']; upload?: Partial<UploadSession> };
}

interface StorySpec {
  key: string;
  title: string;
  summary?: string;
  storyteller: string;
  creator: string;
  date: FuzzyDate;
  location?: string;
  people: Array<[string, StoryPerson['role']]>;
  publishedDaysAgo: number;
  /**
   * The story: one person, one telling. A story never holds a second voice —
   * anything else a relative wants to say about that day is a voice note on
   * the day itself (`voices` below), which is what the plus on the day offers.
   */
  recording: ContribSpec;
  /** What other relatives said about the same day: ordinary voice notes hanging off the story. */
  voices?: VoiceSpec[];
  events?: string[];
  locked?: boolean;
}

/** A voice note somebody left on a story's day — a Moment, not part of the story. */
interface VoiceSpec {
  key: string;
  /** Whose voice it is; they are tagged on the note, the way a face is tagged on a picture. */
  speaker: string;
  /** Whose account put it there. */
  uploader: string;
  seconds: number;
  daysAgo: number;
}

export const STORIES: StorySpec[] = [
  {
    key: 'aliyah',
    title: 'How Saba Moshe came to Israel',
    summary: 'The boat from Marseille, the first night in Haifa, and the suitcase that never arrived.',
    storyteller: 'miriam',
    creator: 'sarah',
    date: about(1949),
    location: 'haifa',
    people: [['miriam', 'storyteller'], ['moshe', 'subject'], ['yaakov', 'mentioned']],
    publishedDaysAgo: 2,
    events: ['aliyah'],
    recording: {
      key: 'aliyah-miriam', speaker: 'miriam', uploader: 'sarah', kind: 'audio', seconds: 14, daysAgo: 2,
      transcript: { lang: 'he', text: 'משה הגיע לחיפה בסתיו, אני חושבת שזה היה 1949. הוא סיפר שהמזוודה נשארה במרסיי ושבלילה הראשון ישנו על הרצפה של בית העולים. בבוקר הוא ראה את הים והחליט שלא יעזוב.', status: 'ready' },
      media: { status: 'backed_up', backup: 'done' },
    },
  },
  {
    key: 'wedding',
    title: 'The wedding in Casablanca',
    storyteller: 'yosef',
    creator: 'david',
    date: decade(1950),
    location: 'casablanca',
    people: [['yosef', 'storyteller'], ['yaakov', 'mentioned'], ['rachel', 'mentioned'], ['moshe', 'subject']],
    publishedDaysAgo: 10,
    recording: {
      key: 'wedding-yosef', speaker: 'yosef', uploader: 'david', kind: 'audio', seconds: 12, daysAgo: 10,
      transcript: { lang: 'he', text: 'החתונה של משה הייתה בחצר של סבא יעקב. אמא רחל בישלה שלושה ימים. אני הייתי ילד קטן, אולי בן עשר, וזוכר בעיקר את המוזיקה ואת הריח של הכוסכוס.', status: 'ready' },
      media: { status: 'backed_up', backup: 'done' },
    },
  },
  {
    key: 'workshop',
    title: 'Grandpa’s workshop on Herzl Street',
    summary: 'Cedar and machine oil, and sweeping the sawdust after school.',
    storyteller: 'david',
    creator: 'david',
    date: about(1970),
    location: 'herzl',
    people: [['david', 'storyteller'], ['leah', 'storyteller'], ['sarah', 'storyteller'], ['moshe', 'subject']],
    publishedDaysAgo: 40,
    events: ['herzl'],
    recording: {
      key: 'workshop-david', speaker: 'david', uploader: 'david', kind: 'audio', seconds: 16, daysAgo: 40,
      transcript: { lang: 'en', text: 'My father opened the workshop around 1970. It smelled of cedar and machine oil. After school I would sweep the sawdust and he would let me sand the small pieces.', status: 'ready' },
      media: { status: 'uploaded', backup: 'pending' },
    },
    /*
     * The demo family's answer to the same day: Leah remembers the year
     * differently and Sarah only knew the workshop as a place for tea. Neither
     * is part of David's telling — they are voice notes left on the day, which
     * is what the plus on a day offers now, and they show up around the story
     * on its constellation.
     */
    voices: [
      { key: 'workshop-leah', speaker: 'leah', uploader: 'david', seconds: 11, daysAgo: 5 },
      { key: 'workshop-sarah', speaker: 'sarah', uploader: 'sarah', seconds: 9, daysAgo: 1 },
    ],
  },
  {
    key: 'eitan-born',
    title: 'The day Eitan was born',
    storyteller: 'david',
    creator: 'david',
    date: ex('1978-03-14'),
    location: 'haifa',
    people: [['david', 'storyteller'], ['eitan', 'subject'], ['dalia', 'participant']],
    publishedDaysAgo: 60,
    events: ['eitan-born'],
    recording: {
      key: 'eitan-born-david', speaker: 'david', uploader: 'david', kind: 'audio', seconds: 10, daysAgo: 60,
      transcript: { lang: 'en', text: 'It rained all night. We took the number 5 bus to the hospital because the taxi never came, and Eitan arrived twenty minutes after we walked in.', status: 'ready' },
      media: { status: 'backed_up', backup: 'done' },
    },
  },
  {
    key: 'noa-adoption',
    title: 'Noa’s adoption day',
    storyteller: 'ruth',
    creator: 'david',
    date: ex('1988-09-02'),
    location: 'netanya',
    people: [['ruth', 'storyteller'], ['noa', 'subject'], ['david', 'participant']],
    publishedDaysAgo: 20,
    recording: {
      key: 'noa-adoption-ruth', speaker: 'ruth', uploader: 'david', kind: 'audio', seconds: 13, daysAgo: 20,
      transcript: { lang: 'he', text: 'ביום שנועה הגיעה הביתה, איתן בן העשר עמד בדלת עם ציור שהכין לה. הוא לא הסכים לזוז עד שהיא צחקה.', status: 'ready' },
      media: { status: 'backed_up', backup: 'done' },
    },
  },
  {
    key: 'flood',
    title: 'When I was about twelve — the flood',
    storyteller: 'miriam',
    creator: 'sarah',
    date: { kind: 'relative', start: '1944', displayText: 'I was about 12', relativePersonId: P('miriam'), relativeAge: 12, certainty: 'approximate' },
    location: 'fes',
    people: [['miriam', 'storyteller']],
    publishedDaysAgo: 5,
    recording: {
      key: 'flood-miriam', speaker: 'miriam', uploader: 'sarah', kind: 'audio', seconds: 15, daysAgo: 5,
      transcript: { lang: 'he', text: 'הייתי בערך בת שתים־עשרה כשהנהר עלה על גדותיו. אבא העלה אותנו לגג עם השמיכות, ומשם ראינו את השכנים שטים בסירות מאולתרות. לא פחדתי — חשבתי שזה משחק.', status: 'ready' },
      media: { status: 'backed_up', backup: 'done' },
    },
  },
  {
    key: 'bike',
    title: 'Lior’s first bike',
    storyteller: 'tom',
    creator: 'tom',
    date: ex('2018-05-20'),
    location: 'netanya',
    people: [['tom', 'storyteller'], ['lior', 'subject'], ['gideon', 'participant']],
    publishedDaysAgo: 3,
    recording: {
      key: 'bike-tom', speaker: 'tom', uploader: 'tom', kind: 'audio', seconds: 8, daysAgo: 3,
      transcript: { lang: 'en', text: 'Grandpa Gideon held the seat, let go without telling him, and Lior rode all the way to the corner before he noticed.', status: 'ready' },
      media: { status: 'backed_up', backup: 'done' },
    },
  },
  {
    key: 'song',
    title: 'A song Savta Rachel used to sing',
    storyteller: 'miriam',
    creator: 'sarah',
    date: { kind: 'unknown' },
    people: [['miriam', 'storyteller'], ['rachel', 'subject']],
    publishedDaysAgo: 90,
    recording: {
      key: 'song-miriam', speaker: 'miriam', uploader: 'sarah', kind: 'audio', seconds: 18, daysAgo: 90,
      transcript: { lang: 'he', text: '', status: 'failed', error: 'Audio too quiet for transcription' },
      media: { status: 'backed_up', backup: 'done' },
    },
  },
  {
    key: 'passover',
    title: 'Passover 2024 — the whole table',
    storyteller: 'noa',
    creator: 'noa',
    date: ex('2024-04-22'),
    location: 'netanya',
    people: [['noa', 'storyteller'], ['maya', 'storyteller'], ['miriam', 'participant'], ['ori', 'participant']],
    publishedDaysAgo: 8,
    recording: {
      key: 'passover-noa', speaker: 'noa', uploader: 'noa', kind: 'audio', seconds: 9, daysAgo: 8,
      transcript: { lang: 'he', text: 'סבתא מרים התעקשה לשיר את "חד גדיא" בערבית־יהודית, ואורי ניסה לחזור אחריה. זו הפעם הראשונה שכולנו ישבנו יחד מאז הקורונה.', status: 'ready' },
      media: { status: 'paused_retry', backup: 'none', upload: { state: 'paused', bytesUploaded: 61_000, attempts: 2, lastError: 'Connection lost during upload' } },
    },
    voices: [{ key: 'passover-maya', speaker: 'maya', uploader: 'noa', seconds: 7, daysAgo: 6 }],
  },
  {
    key: 'montreal',
    title: 'Why Nathalie moved to Montreal',
    storyteller: 'david2',
    creator: 'daniel',
    date: month('1987-02'),
    location: 'montreal',
    people: [['david2', 'storyteller'], ['nathalie', 'subject'], ['shimon', 'mentioned']],
    publishedDaysAgo: 120,
    recording: {
      key: 'montreal-david2', speaker: 'david2', uploader: 'daniel', kind: 'audio', seconds: 11, daysAgo: 120,
      transcript: { lang: 'en', text: 'My sister took a winter job in Montreal for one season. Father said she would be back by spring. That was 1987.', status: 'ready' },
      media: { status: 'action_needed', backup: 'failed', upload: { state: 'action_needed', bytesUploaded: 0, attempts: 5, lastError: 'Storage quota exceeded for this family' } },
    },
  },
];

export const EVENTS: Array<{ key: string; title: string; titleHe: string; date: FuzzyDate; location?: string; status: FamilyEvent['status']; locked?: boolean }> = [
  { key: 'aliyah', title: 'Moshe’s arrival in Israel', titleHe: 'עליית משה לישראל', date: about(1949), location: 'haifa', status: 'approximate' },
  { key: 'wedding', title: 'Wedding of Moshe and Miriam', titleHe: 'חתונת משה ומרים', date: month('1951-06'), location: 'haifa', status: 'confirmed', locked: true },
  { key: 'herzl', title: 'Workshop opens on Herzl Street', titleHe: 'פתיחת הנגרייה ברחוב הרצל', date: about(1970), location: 'herzl', status: 'approximate' },
  { key: 'eitan-born', title: 'Eitan is born', titleHe: 'איתן נולד', date: ex('1978-03-14'), location: 'haifa', status: 'confirmed' },
  { key: 'moshe-died', title: 'Moshe passes away', titleHe: 'משה נפטר', date: ex('2003-11-09'), status: 'confirmed' },
];

export interface StorySeed {
  stories: Story[];
  contributions: StoryContribution[];
  /** Voice notes left on a story's day — ordinary Moments, the same as a photograph added to it. */
  photos: FamilyPhoto[];
  storyPeople: StoryPerson[];
  events: FamilyEvent[];
  storyEvents: StoryEvent[];
  media: MediaAsset[];
  uploads: UploadSession[];
  transcripts: Transcript[];
  drafts: RecordingDraft[];
  suggestions: StorySuggestion[];
}

export async function buildStories(): Promise<StorySeed> {
  const out: StorySeed = { stories: [], contributions: [], photos: [], storyPeople: [], events: [], storyEvents: [], media: [], uploads: [], transcripts: [], drafts: [], suggestions: [] };

  for (const e of EVENTS) {
    out.events.push({ id: E(e.key), familyId: FAMILY_ID, title: e.title, titleHe: e.titleHe, date: e.date, locationId: e.location ? L(e.location) : undefined, status: e.status, locked: e.locked ?? false, version: 1, createdAt: daysAgoISO(300), updatedAt: daysAgoISO(300), updatedBy: A('sarah') });
  }

  let seedNo = 1;
  for (const s of STORIES) {
    const publishedAt = daysAgoISO(s.publishedDaysAgo, 3);
    out.stories.push({
      id: S(s.key), familyId: FAMILY_ID, title: s.title, titlePending: false, summary: s.summary, date: s.date,
      locationId: s.location ? L(s.location) : undefined, status: 'published',
      creatorAccountId: A(s.creator), primaryStorytellerPersonId: P(s.storyteller), publishedAt, locked: s.locked ?? false,
      idempotencyKey: `seed:story:${s.key}`, version: 1, createdAt: publishedAt, updatedAt: publishedAt, updatedBy: A(s.creator),
    });
    for (const [pk, role] of s.people) out.storyPeople.push({ id: seedId('sp', `${s.key}:${pk}`), storyId: S(s.key), personId: P(pk), role });
    for (const ek of s.events ?? []) out.storyEvents.push({ id: seedId('se', `${s.key}:${ek}`), storyId: S(s.key), eventId: E(ek) });

    // One recording, always: the story is the telling, and `order` is kept only because the row has the field.
    const c = s.recording;
    const at = daysAgoISO(c.daysAgo, 2);
    let mediaId: UUID | undefined;
    if (c.kind === 'audio') {
      mediaId = M(c.key);
      const blob = placeholderWav(c.seconds ?? 10, seedNo++);
      await putBlob(mediaKey(mediaId), blob);
      out.media.push({
        id: mediaId, familyId: FAMILY_ID, kind: 'audio', role: 'original', mimeType: 'audio/wav', durationMs: (c.seconds ?? 10) * 1000,
        sizeBytes: blob.size, storageKey: mediaKey(mediaId), language: c.transcript?.lang ?? 'unknown', speakerPersonId: P(c.speaker),
        uploaderAccountId: A(c.uploader), status: c.media?.status ?? 'backed_up', backupState: c.media?.backup ?? 'done', createdAt: at, updatedAt: at, isPlaceholder: true,
      });
      if (c.media?.upload) {
        out.uploads.push({
          id: seedId('upload', c.key), mediaAssetId: mediaId, localDraftId: seedId('draft', c.key), idempotencyKey: `seed:upload:${c.key}`, totalBytes: blob.size,
          bytesUploaded: 0, state: 'paused', attempts: 0, createdAt: at, updatedAt: at, ...c.media.upload,
        });
      }
    }
    out.contributions.push({
      id: C(c.key), storyId: S(s.key), speakerPersonId: P(c.speaker), uploaderAccountId: A(c.uploader), order: 0, kind: c.kind,
      mediaAssetId: mediaId, text: c.text, dateInterpretation: c.dateInterpretation, status: 'published', idempotencyKey: `seed:contribution:${c.key}`,
      version: 1, createdAt: at, updatedAt: at, updatedBy: A(c.uploader),
    });
    if (c.transcript) {
      out.transcripts.push({
        id: seedId('transcript', c.key), contributionId: C(c.key), language: c.transcript.lang, text: c.transcript.text, isTranslation: false, source: 'ai',
        status: c.transcript.status, reviewStatus: c.transcript.status === 'ready' ? 'approved' : 'unreviewed', version: 1, updatedAt: at, error: c.transcript.error,
      });
    }

    /*
     * And what everyone else said about that day. These are Moments, not parts
     * of the story: the same record a photograph or a clip added to the day
     * would be, carrying `addedTo`, so they appear as nodes around the story on
     * its constellation, in the Moments tab, and under the circle of whoever
     * spoke — and the story itself stays one telling.
     */
    for (const v of s.voices ?? []) {
      const vAt = daysAgoISO(v.daysAgo, 2);
      const id = seedId('photo', `voice:${v.key}`);
      const blob = placeholderWav(v.seconds, seedNo++);
      const key = `photo/${id}`;
      await putBlob(key, blob);
      out.photos.push({
        id, familyId: FAMILY_ID, uploadedByAccountId: A(v.uploader), storageKey: key, mimeType: 'audio/wav', bytes: blob.size,
        durationMs: v.seconds * 1000, isEvent: false, addedTo: { kind: 'story', id: S(s.key) }, takenOn: s.date,
        personIds: [P(v.speaker)], createdAt: vAt,
      });
    }
  }

  // An unfinished local draft for Sarah (section 12.3: "save a private draft and return later")
  out.drafts.push({
    id: seedId('draft', 'sarah-netanya'), accountId: A('sarah'), storyId: null, speakerPersonId: P('sarah'), title: 'Summer in Netanya', titlePending: false,
    date: about(1995, 'the summer I was about 11'), peopleIds: [P('sarah'), P('noa'), P('ruth')], locationId: L('netanya'),
    durationMs: 0, mimeType: 'audio/webm', chunkCount: 0, step: 1, createdAt: daysAgoISO(4), updatedAt: daysAgoISO(4),
  });

  // Somebody who did not record the story thinks the year is wrong. Sarah, who
  // recorded it, is the one who decides — so signing in as her shows the review
  // waiting on the story, and signing in as Daniel shows his own suggestion.
  out.suggestions.push({
    id: seedId('suggestion', 'aliyah-year'),
    familyId: FAMILY_ID,
    storyId: S('aliyah'),
    change: { kind: 'date', date: yr(1950) },
    note: 'Savta always said they landed the spring after the boat, so I think it was 1950.',
    suggestedByAccountId: A('daniel'),
    status: 'open',
    createdAt: daysAgoISO(1),
  });

  return out;
}
